import { useCopilotChatInternal, useFrontendTool } from '@copilotkit/react-core';
import { Button, Card, Group, Stack, Text, Title } from '@mantine/core';

import { usePipelineTemplates } from 'pipelines/hooks/usePipelineTemplates';

import { PipelineTemplateSmallCard } from 'analytics-library/components/PipelineTemplateSmallCard/PipelineTemplateSmallCard';

type SearchTemplatesRenderProps = {
  status: 'complete' | 'executing' | 'inProgress';
  args: { query: string };
};

function SearchTemplatesRenderer({ status, args }: SearchTemplatesRenderProps) {
  const { sendMessage } = useCopilotChatInternal();
  const query = args.query;

  const { data, isLoading, error } = usePipelineTemplates({
    params: { query, limit: 10 },
  });

  const handleCreateCustom = () => {
    sendMessage({
      id: crypto.randomUUID(),
      role: 'user',
      content: `I don't want to use a template. Please create a custom workflow for: ${query}`,
    });
  };

  if (status === 'inProgress' || status === 'executing' || isLoading) {
    return (
      <Card withBorder p="md" radius="md">
        <Text c="dimmed" size="sm">
          Searching for pipeline templates...
        </Text>
      </Card>
    );
  }

  if (error) {
    return (
      <Card withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text c="red" size="sm">
            Failed to search templates
          </Text>
          <Group>
            <Button size="xs" variant="outline" onClick={handleCreateCustom}>
              Create Custom Workflow Instead
            </Button>
          </Group>
        </Stack>
      </Card>
    );
  }

  const templates = data?.data || [];

  if (templates.length === 0) {
    return (
      <Card withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            No pre-built templates found for "{query}"
          </Text>
          <Group>
            <Button size="xs" onClick={handleCreateCustom}>
              Create Custom Workflow
            </Button>
          </Group>
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder p="md" radius="md">
      <Stack gap="sm">
        <Title order={5}>Pre-built Pipeline Templates</Title>
        <Text size="sm" c="dimmed">
          Found {templates.length} template
          {templates.length !== 1 ? 's' : ''} matching "{query}".
        </Text>

        <Stack gap="xs">
          {templates.map((template) => (
            <PipelineTemplateSmallCard key={template.id} template={template} />
          ))}
        </Stack>

        <Group>
          <Button size="xs" variant="outline" onClick={handleCreateCustom}>
            Create Custom Workflow Instead
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

export function useSearchPipelineTemplatesTool() {
  useFrontendTool(
    {
      name: 'searchPipelineTemplates',
      description:
        "Search for pre-built pipeline (aka workflow) templates that match a user's requirements. ALWAYS use this tool FIRST when a user asks about or wants to deploy, import or create a new pipeline, before using generate_pipeline_definition tool. This tool presents matching templates to the user, so you dont need to describe the templates. Only proceed with generate_pipeline_definition if the user explicitly declines the templates or no templates are found.",
      parameters: [
        {
          name: 'query',
          type: 'string',
          description:
            'Search query describing the type of pipeline the user wants (e.g. "object detection", "face recognition", "license plate")',
          required: true,
        },
      ],
      handler: async ({ query }: { query: string }) => {
        // The actual data fetching happens in the renderer using usePipelineTemplates hook
        // This handler just acknowledges the tool call
        return { query, message: 'Templates displayed to user' };
      },
      render: ({ status, args }) => (
        <SearchTemplatesRenderer
          status={status}
          args={args as { query: string }}
        />
      ),
    },
    []
  );
}
